'use client';

import { BagguLogo, BranchLogo, ClickupLogo, EchoLogo, GoDaddyLogo } from '@/components/_rebrand/logos/index';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useRef, useState } from 'react';


const DESKTOP_BREAKPOINT = 800;
const AUTOPLAY_INTERVAL = 20000;

export interface HomePageFeatureCarouselProps {
  title?: string;
  description?: string;
  masterMarginsTop?: boolean;
  masterMarginsBottom?: boolean;

  features: {
    title: string;
    description: string;
    image: React.ReactNode;
    icon: React.ReactNode;
  }[];
}

const HomePageFeatureCarousel: FC<HomePageFeatureCarouselProps> = ({ title, description, features = [], masterMarginsTop, masterMarginsBottom }) => {
  const currentSlideTimeout = useRef<any>(null);
  const [activeFeatureIndex, setFeatureIndex] = useState(0);
  const activeFeature = features[activeFeatureIndex];

  const header = (
    <div className="flex">
      <div className="md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 xl:col-span-8 xl:col-start-3 flex flex-col gap-3">
        <h2 className="font-500 tracking-tight text-30/36 md:text-52/62">{title}</h2>
        <p className="text-17/24 text-gray-700">{description}</p>
      </div>
    </div>
  );

  const mobileGrid = (
    <div className="flex flex-col gap-y-6 md:hidden">
      {features.map((feature, index) => (
        <div key={`feature-mobile-${feature.title?.replace(/\s+/g, '-') ?? index}`} className="flex flex-col gap-y-2">
          <div className="relative aspect-[1222/688] rounded-10 overflow-hidden">{feature.image}</div>

          <div className="flex gap-1 items-center">
            {feature.icon}
            <p className="text-14/20 font-500">{feature.title}</p>
          </div>

          <p className="text-14/20 text-gray-700">{feature.description}</p>
        </div>
      ))}
    </div>
  );

  const desktopNav = (
    <div className="flex gap-1 items-center justify-between w-full max-w-[1140px] mb-[16px] mt-[75px]">
      {features.map((feature, index) => (
        <div key={`feature-desktop-nav-${feature.title?.replace(/\s+/g, '-') ?? index}`} className="max-w-[205px] w-full relative flex flex-col gap- items-center">
          <button
            className={cx('block button button-size-medium button-drawer-violet', {
              active: index === activeFeatureIndex,
            })}
            onClick={() => setFeatureIndex(index)}
          >
            <div className="flex items-center gap-1">
              {feature.icon}
              <div className="text-caption1">{feature.title}</div>
            </div>
          </button>

          <div className={`h-[6px] mt-[12px] w-full rounded-10 overflow-hidden ${index === activeFeatureIndex ? "bg-violet-600" : "bg-violet-800"} bg-violet-600 relative`}>
            <AnimatePresence mode="wait">
              {index === activeFeatureIndex && (
                <motion.div
                  initial={{ width: 0 }}
                  className="absolute inset-0 h-[6px] rounded-10 overflow-hidden bg-violet-600"
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      ))}
    </div>
  );

  const desktopActiveSlide = (
    <div className="hidden md:block w-full aspect-[1253/652] relative flex justify-center max-w-[1254px]">
      <AnimatePresence mode="wait">
        {activeFeature && (
          <motion.div
            key={activeFeatureIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}

          >
            <div className="flex w-full max-w-[1253px]">
              <div className="col-span-12 lg:col-span-10 lg:col-start-2">
                <div className="rounded-10 overflow-hidden">{activeFeature.image}</div>
              </div>
              <div className="col-span-10 col-start-2 lg:col-span-6 lg:col-start-4">
                <p className="text-14/20 text-violet-600 text-center">{activeFeature.description}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div className={`${masterMarginsTop ? `mt-[48px] ` : "mt-0"} ${masterMarginsBottom ? `mb-[24px] ` : "mb-8"}`}>

      <div className="flex w-full flex-wrap justify-center">
        <div className="hidden md:flex flex-wrap justify-center">
          {desktopNav}
          {desktopActiveSlide}
        </div>
        <div className="hidden md:flex w-full items-center justify-between mt-[54px]">
          <div className="flex items-center">
            <div className="flex flex-wrap text-violet-200 text-caption1">
              <span className="font-500 text-p2 mr-[6px]">
                Trusted by 9,000+
              </span>
              <span className="text-p2">
                companies worldwide
              </span>
            </div>
          </div>
          <div className="flex items-center">
            <div className="flex mr-[20px] 2xl:mr-[70px]">
              {BranchLogo()}
            </div>
            <div className="flex mr-[20px] 2xl:mr-[70px]">
              {EchoLogo()}
            </div>
            <div className="flex mr-[20px] 2xl:mr-[70px]">
              {ClickupLogo()}
            </div>
            <div className="flex mr-[20px] 2xl:mr-[70px]">
              {GoDaddyLogo()}
            </div>
            <div className="flex">
              {BagguLogo()}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default HomePageFeatureCarousel;
